import React from 'react';
import flat from 'flat';
import { DeepDiff } from 'deep-diff';
import { isEmpty, toString } from 'lodash';
import { normalizeDiffItem, normalizePath } from './normalize';
import { DIFF_KIND, DIFF_EXCLUDED_KEYS } from './constants';

const isValueEmpty = (value) =>
  typeof value !== 'number' && typeof value !== 'boolean' && isEmpty(value);

const prefilterDiffItem = (_, key) => DIFF_EXCLUDED_KEYS.includes(key);

/**
 * Generate a change log for the revision using previous and updated data.
 */
export const getChangeLog = (revision) => {
  // Generate a list of changes
  const changeList = DeepDiff(
    revision.prevDocumentData,
    revision.documentData,
    { normalize: normalizeDiffItem, prefilter: prefilterDiffItem },
  );

  // Return empty list if no changes found
  if (!changeList) return [];
  return (
    changeList
      .map((diffItem) => {
        // Flat array changes
        if (diffItem.kind === DIFF_KIND.ARRAY) {
          return {
            ...diffItem.item,
            path: diffItem.path.concat(diffItem.index),
          };
        }
        return diffItem;
      })
      .reduce((acc, item) => {
        // Flat new nested objects that don't have a corresponding previous value
        if (
          typeof item.rhs === 'object' &&
          !isEmpty(item.rhs) &&
          isEmpty(item.lhs)
        ) {
          const flattened = flat(item.rhs);
          const keys = Object.keys(flattened);
          acc.push(
            ...keys.map((key) => ({
              ...item,
              path: item.path.concat(key),
              rhs: flattened[key],
            })),
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, [])
      // Filter out empty changes
      .filter((item) => !isValueEmpty(item.lhs) || !isValueEmpty(item.rhs))
      .map((item) => {
        // Stringify values
        const prevValue = toString(item.lhs);
        const nextValue = toString(item.rhs);
        const prev = <span className="diff-prev">{prevValue}</span>;
        const next = <span className="diff-next">{nextValue}</span>;
        // Generate changed path
        const path = (
          <span className="diff-path">
            {normalizePath(item.path).join(' → ')}
          </span>
        );

        // Format the change
        if (item.kind === DIFF_KIND.NEW || (!prevValue && nextValue)) {
          return (
            <span>
              Updated {path} to {next}.
            </span>
          );
        }
        if (item.kind === DIFF_KIND.EDIT) {
          return (
            <span>
              Updated {path} from {prev} to {next}.
            </span>
          );
        }
        if (item.kind === DIFF_KIND.DELETE) {
          return <span>Removed {path}.</span>;
        }

        return '';
      })
  );
};
